import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { formatBRL, formatDateBR, FUEL_TYPES, PAYMENT_METHODS } from '../lib/format';
import { formatPhoneBR } from '../components/PhoneInput';
import { formatDocBR } from '../components/DocumentInput';

function dateLong(iso) {
  if (!iso) return '';
  const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return `${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
}

function fuelLabel(code) {
  if (!code) return '';
  return FUEL_TYPES?.[code] || code;
}

function paymentMethodLabel(code) {
  if (!code) return '';
  return PAYMENT_METHODS?.[code] || code;
}

function tradeLabel(p) {
  if (p?.method !== 'TRADE') return '—';
  if (p?.trade_vehicle_label) return p.trade_vehicle_label;
  const t = p?.trade_vehicle || {};
  const parts = [t.brand, t.model, t.version].filter(Boolean).join(' ');
  return parts ? `${parts}${t.plate ? ` (Placa ${t.plate})` : ''}` : 'Veículo de troca';
}

export default function VehicleContract() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/vehicles/${id}/summary`);
        setV(data.vehicle || data);
      } catch (e) {
        setV(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (v) {
      const brand = (v.brand || '').trim();
      const model = (v.model || '').trim();
      const plate = (v.plate || '').trim();
      document.title = `CCV - ${brand} ${model} - ${plate}`.replace(/\s+/g, ' ').trim();
    } else {
      document.title = 'Contrato de Compra e Venda';
    }
  }, [v]);

  if (loading) return <div style={{ padding: 40, fontFamily: 'serif' }}>Carregando contrato…</div>;
  if (!v) return <div style={{ padding: 40, fontFamily: 'serif' }}>Veículo não encontrado.</div>;

  const payments = v.sale_payments || [];

  return (
    <>
      <style>{`
        @page { size: A4; margin: 22mm 22mm 22mm 22mm; }
        body { background: #e9ecef; margin: 0; }
        :root {
          --ink: #0f172a;
          --rule: #1f2937;
          --muted: #4b5563;
          --soft: #e5e7eb;
          --accent: #0f172a;
        }
        .doc-wrap {
          max-width: 800px;
          margin: 28px auto;
          background: white;
          color: var(--ink);
          font-family: 'Cambria', 'Georgia', 'Times New Roman', serif;
          font-size: 11pt;
          line-height: 1.55;
          padding: 56px 64px 72px;
          box-shadow: 0 4px 24px rgba(15,23,42,0.10);
          position: relative;
        }
        .doc-print-toolbar {
          position: fixed; top: 12px; right: 12px;
          display: flex; gap: 8px; z-index: 50;
        }
        .doc-print-toolbar button {
          background: var(--ink); color: white; border: none;
          font: 600 12px/1 'Inter', system-ui, sans-serif;
          padding: 10px 14px; border-radius: 6px; cursor: pointer;
          letter-spacing: 0.4px; text-transform: uppercase;
        }
        .doc-print-toolbar button:hover { background: #1f2937; }
        .doc-print-toolbar button.ghost {
          background: white; color: var(--ink); border: 1px solid var(--soft);
        }

        .doc-header {
          display: flex; justify-content: space-between; align-items: flex-end;
          border-bottom: 2px solid var(--rule);
          padding-bottom: 14px; margin-bottom: 26px;
        }
        .doc-header .brand {
          font: 700 18pt/1 'Cambria', 'Georgia', serif;
          letter-spacing: 1.6px;
        }
        .doc-header .brand small {
          display: block; font: 400 9pt/1.4 'Cambria', serif;
          color: var(--muted); letter-spacing: 0.8px; margin-top: 4px;
          text-transform: uppercase;
        }
        .doc-header .meta {
          text-align: right; font-size: 9pt; color: var(--muted);
          line-height: 1.45;
        }

        .doc-title {
          text-align: center; margin: 8px 0 28px;
        }
        .doc-title h1 {
          font: 700 15pt/1.25 'Cambria', serif;
          letter-spacing: 1.5px; text-transform: uppercase; margin: 0;
        }
        .doc-title .subtitle {
          font: 400 10pt/1.4 'Cambria', serif;
          letter-spacing: 0.6px; color: var(--muted);
          margin-top: 4px; text-transform: uppercase;
        }

        .section { margin-top: 18px; }
        .section h2 {
          font: 700 10.5pt/1 'Cambria', serif;
          letter-spacing: 1.6px; text-transform: uppercase;
          margin: 0 0 10px; padding: 0 0 4px;
          border-bottom: 1px solid var(--rule);
          color: var(--ink);
        }

        .party-block {
          margin: 8px 0 4px;
          padding: 0;
        }
        .party-block p { margin: 0 0 6px; text-align: justify; }

        .data-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2px 24px;
          margin: 8px 0 4px;
        }
        .data-grid .row {
          padding: 4px 0;
          border-bottom: 1px dotted #cbd5e1;
        }
        .data-grid .row .lbl {
          font: 600 9pt/1.4 'Cambria', serif;
          color: var(--muted);
          text-transform: uppercase;
          letter-spacing: 0.6px;
          display: block;
        }
        .data-grid .row .val {
          font-size: 11pt;
          color: var(--ink);
        }
        .data-grid .row.full { grid-column: 1 / -1; }

        .pay-table {
          width: 100%; border-collapse: collapse; margin-top: 8px;
          font-size: 10.5pt;
        }
        .pay-table thead th {
          font: 600 9pt/1.2 'Cambria', serif;
          letter-spacing: 0.6px; text-transform: uppercase;
          color: var(--muted); text-align: left;
          padding: 6px 8px; border-bottom: 1.5px solid var(--rule);
        }
        .pay-table tbody td {
          padding: 8px; border-bottom: 1px solid var(--soft);
          vertical-align: top;
        }
        .pay-table tbody tr.total td {
          background: #f3f4f6; font-weight: 700;
          border-top: 1.5px solid var(--rule);
          border-bottom: none;
        }
        .pay-table tbody td.num { font-feature-settings: 'tnum'; text-align: right; }

        .total-banner {
          margin: 4px 0 0; padding: 10px 14px;
          background: #0f172a; color: white;
          display: flex; justify-content: space-between; align-items: baseline;
          letter-spacing: 0.4px;
        }
        .total-banner .lbl { font: 600 10pt/1 'Cambria', serif; text-transform: uppercase; letter-spacing: 1.2px; }
        .total-banner .val { font: 700 13pt/1 'Cambria', serif; }

        .clauses { margin-top: 22px; }
        .clauses h3 {
          font: 700 10.5pt/1.3 'Cambria', serif;
          letter-spacing: 1.4px; text-transform: uppercase;
          margin: 18px 0 6px; color: var(--ink);
        }
        .clauses p {
          text-align: justify; margin: 4px 0;
          text-indent: 0;
        }
        .clauses p + p { margin-top: 4px; }

        .signature-section {
          margin-top: 8px; page-break-inside: avoid;
        }
        .signature-section .place {
          margin: 16px 0 48px; text-align: center; font-size: 10.5pt;
        }
        .sig-row {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 64px; margin-top: 12px;
        }
        .sig-box { text-align: center; }
        .sig-line {
          border-top: 1px solid var(--ink);
          padding-top: 6px;
          font-size: 10pt;
          line-height: 1.4;
        }
        .sig-name {
          font: 700 10.5pt/1.2 'Cambria', serif;
          text-transform: uppercase; letter-spacing: 0.6px;
        }
        .sig-role {
          font-size: 9pt; color: var(--muted);
          letter-spacing: 0.5px; text-transform: uppercase; margin-top: 2px;
        }
        .sig-doc {
          font-size: 9pt; color: var(--muted); margin-top: 2px;
        }

        .witness-section {
          margin-top: 56px; page-break-inside: avoid;
        }
        .witness-section h4 {
          font: 700 9.5pt/1 'Cambria', serif;
          letter-spacing: 1.4px; text-transform: uppercase;
          margin: 0 0 24px; color: var(--muted);
        }

        .doc-footer {
          margin-top: 40px; padding-top: 12px;
          border-top: 1px solid var(--soft);
          font-size: 8.5pt; color: var(--muted); text-align: center;
          letter-spacing: 0.3px;
        }

        @media print {
          body { background: white; }
          .doc-wrap {
            box-shadow: none; margin: 0; padding: 0;
            max-width: none;
          }
          .doc-print-toolbar { display: none !important; }
          .clauses h3, .signature-section, .witness-section { page-break-inside: avoid; }
        }
      `}</style>

      <div className="doc-print-toolbar">
        <button onClick={() => window.print()}>Imprimir / Salvar PDF</button>
        <button className="ghost" onClick={() => window.close()}>Fechar</button>
      </div>

      <div className="doc-wrap">
        <div className="doc-header">
          <div className="brand">
            MATELLI MOTORS
            <small>Compra e venda de veículos</small>
          </div>
          <div className="meta">
            CNPJ 62.352.611/0001-56<br />
            Ribeirão Preto/SP · CEP 14.021-612<br />
            {v.sale_date ? <>Emitido em {formatDateBR(v.sale_date)}<br /></> : ''}
            <div style={{
              marginTop: 8, padding: '4px 8px',
              border: '1px solid var(--rule)', borderRadius: 4,
              fontFamily: 'monospace', fontSize: '9pt', letterSpacing: '0.5px',
              display: 'inline-block', background: '#f8fafc'
            }}>
              CONTRATO Nº {String(v.id || '').slice(0, 8).toUpperCase()}
            </div>
          </div>
        </div>

        <div className="doc-title">
          <h1>Contrato de compra e venda de veículo usado</h1>
          <div className="subtitle">Instrumento particular</div>
        </div>

        <div className="section">
          <h2>I — Partes</h2>
          <div className="party-block">
            <p>
              <strong>VENDEDORA:</strong> MATELLI MOTORS LTDA, pessoa jurídica de direito privado,
              inscrita no CNPJ sob nº <strong>62.352.611/0001-56</strong>, com sede na
              Rua Triunfo, nº 1.349, sala 315, Jardim Botânico, Ribeirão Preto/SP, CEP 14.021-612,
              doravante denominada simplesmente VENDEDORA.
            </p>
            <p style={{ marginTop: 10 }}>
              <strong>COMPRADOR:</strong> {v.buyer_name || '—'}, portador do CPF/CNPJ
              nº <strong>{v.buyer_document ? formatDocBR(v.buyer_document) : '—'}</strong>,
              telefone {v.buyer_phone ? formatPhoneBR(v.buyer_phone) : '—'}, e-mail {v.buyer_email || '—'},
              doravante denominado simplesmente COMPRADOR.
            </p>
          </div>
        </div>

        <div className="section">
          <h2>II — Objeto</h2>
          <p style={{ margin: '6px 0 8px' }}>
            Constitui objeto deste contrato a compra e venda do veículo abaixo identificado,
            doravante denominado simplesmente VEÍCULO.
          </p>
          <div className="data-grid">
            <div className="row"><span className="lbl">Marca</span><span className="val">{v.brand || '—'}</span></div>
            <div className="row"><span className="lbl">Modelo</span><span className="val">{v.model || '—'}</span></div>
            <div className="row"><span className="lbl">Versão</span><span className="val">{v.version || '—'}</span></div>
            <div className="row"><span className="lbl">Cor</span><span className="val">{v.color || '—'}</span></div>
            <div className="row"><span className="lbl">Ano modelo</span><span className="val">{v.year || '—'}</span></div>
            <div className="row"><span className="lbl">Ano de fabricação</span><span className="val">{v.year_fabrication || '—'}</span></div>
            <div className="row"><span className="lbl">Combustível</span><span className="val">{fuelLabel(v.fuel_type)}</span></div>
            <div className="row"><span className="lbl">Quilometragem</span><span className="val">{v.km != null ? `${Number(v.km).toLocaleString('pt-BR')} km` : '—'}</span></div>
            <div className="row"><span className="lbl">Placa</span><span className="val">{v.plate || '—'}</span></div>
            <div className="row"><span className="lbl">Renavam</span><span className="val">{v.renavam || '—'}</span></div>
            <div className="row full"><span className="lbl">Chassi</span><span className="val">{v.chassis || '—'}</span></div>
            {v.sale_notes && (
              <div className="row full"><span className="lbl">Observações do negócio</span><span className="val" style={{ whiteSpace: 'pre-wrap' }}>{v.sale_notes}</span></div>
            )}
          </div>
        </div>

        <div className="section">
          <h2>III — Preço e formas de pagamento</h2>
          <div className="total-banner">
            <span className="lbl">Valor total da operação</span>
            <span className="val">{formatBRL(v.sale_price || 0)}</span>
          </div>
          {payments.length > 0 ? (
            <>
              <table className="pay-table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>#</th>
                    <th>Forma</th>
                    <th>Data</th>
                    <th>Detalhe</th>
                    <th style={{ textAlign: 'right' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => {
                    const isNegative = Number(p.amount) < 0;
                    return (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{paymentMethodLabel(p.method)}</td>
                        <td>{p.date ? formatDateBR(p.date) : '—'}</td>
                        <td>
                          {p.method === 'TRADE' ? tradeLabel(p) : (p.notes || '—')}
                          {isNegative && <span style={{ color: '#b91c1c', fontWeight: 600, marginLeft: 6 }}>(Devolução ao comprador)</span>}
                        </td>
                        <td className="num" style={isNegative ? { color: '#b91c1c', fontWeight: 600 } : {}}>
                          {formatBRL(p.amount || 0)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="total">
                    <td colSpan={4}>Total</td>
                    <td className="num">{formatBRL(payments.reduce((s, p) => s + (Number(p.amount) || 0), 0))}</td>
                  </tr>
                </tbody>
              </table>
              {payments.some(p => Number(p.amount) < 0) && (
                <div style={{
                  marginTop: 12,
                  padding: '10px 14px',
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: 6,
                  fontSize: '10pt',
                  lineHeight: 1.5
                }}>
                  <strong>⚠️ Troca com troco:</strong> A VENDEDORA efetuará devolução em dinheiro ao COMPRADOR no valor indicado como negativo, em razão do valor do veículo recebido em troca superar o valor do VEÍCULO ora vendido.
                </div>
              )}
            </>
          ) : (
            <p style={{ margin: '8px 0', fontStyle: 'italic', color: 'var(--muted)' }}>
              Nenhuma forma de pagamento registrada.
            </p>
          )}
          <p style={{ marginTop: 10 }}>
            O total das formas de pagamento corresponde integralmente ao valor da venda indicado no caput.
          </p>
        </div>

        <div className="clauses">
          <h2 style={{
            font: '700 10.5pt/1 Cambria, serif',
            letterSpacing: '1.6px', textTransform: 'uppercase',
            margin: '20px 0 10px', padding: '0 0 4px',
            borderBottom: '1px solid var(--rule)'
          }}>IV — Cláusulas e condições</h2>

          <h3>Cláusula 1ª — Objeto</h3>
          <p>1.1. A VENDEDORA vende ao COMPRADOR o VEÍCULO identificado no item II deste contrato, pelo valor e mediante as formas de pagamento descritas no item III.</p>
          <p>1.2. O VEÍCULO é usado, possuindo características, sinais de utilização e desgaste compatíveis com seu ano, quilometragem, histórico e estado geral de conservação.</p>
          <p>1.3. A quilometragem indicada corresponde àquela exibida no painel na data do registro da operação, podendo sofrer pequena alteração em razão de movimentações necessárias para guarda, vistoria, manutenção, abastecimento, regularização ou entrega do VEÍCULO.</p>
          <p>1.4. Integram a contratação, quando existentes, a nota fiscal, os comprovantes de pagamento, o laudo cautelar, o registro fotográfico, os documentos de transferência, as ordens de serviço e as informações objetivas fornecidas pela VENDEDORA durante a negociação.</p>

          <h3>Cláusula 2ª — Preço e formas de pagamento</h3>
          <p>2.1. O preço total da compra e venda é aquele indicado no caput, devendo a soma das formas de pagamento corresponder integralmente ao valor da operação.</p>
          <p>2.2. Os pagamentos serão considerados realizados somente após: a) a efetiva compensação e disponibilidade dos valores na conta da VENDEDORA, quando realizados por PIX, transferência bancária ou outro meio eletrônico; b) a efetiva liberação e pagamento do crédito à VENDEDORA, quando realizados por financiamento, consórcio ou instituição financeira; c) a entrega física e documental regular do veículo oferecido em troca, quando essa modalidade integrar o pagamento; ou d) a confirmação definitiva da operação pela respectiva administradora, quando utilizado cartão ou outro meio de pagamento sujeito a aprovação.</p>
          <p>2.3. Comprovante de agendamento, proposta de financiamento, pré-aprovação de crédito, limite disponível, simulação ou simples envio de comprovante não equivalem à quitação.</p>
          <p>2.4. A quitação integral somente será concedida depois do efetivo recebimento de todas as parcelas e formas de pagamento indicadas no caput.</p>
          <p>2.5. Caso alguma forma de pagamento não seja concluída, recusada, estornada, cancelada ou não compensada, o COMPRADOR continuará responsável pelo respectivo valor.</p>

          <h3>Cláusula 3ª — Financiamento, consórcio ou crédito</h3>
          <p>3.1. Esta cláusula será aplicável quando uma das formas de pagamento descritas no caput envolver financiamento, consórcio, carta de crédito ou outra operação de crédito.</p>
          <p>3.2. A aprovação preliminar da operação pela instituição financeira não representa pagamento nem obriga a VENDEDORA a entregar o VEÍCULO antes da efetiva liberação do valor.</p>
          <p>3.3. O COMPRADOR deverá fornecer à instituição financeira informações verdadeiras e documentos válidos, bem como assinar os instrumentos necessários à conclusão do crédito.</p>
          <p>3.4. A entrega definitiva do VEÍCULO poderá ser condicionada: a) à aprovação final da operação; b) à assinatura do contrato financeiro; c) ao registro do gravame, quando aplicável; e d) ao efetivo pagamento do valor à VENDEDORA.</p>
          <p>3.5. Se o crédito não for liberado por fato imputável ao COMPRADOR, ele deverá substituir a respectiva forma de pagamento no prazo de 3 (três) dias úteis contados da comunicação da VENDEDORA.</p>
          <p>3.6. Não ocorrendo a substituição, a VENDEDORA poderá resolver o contrato, devolver os valores efetivamente recebidos, descontadas as obrigações legalmente permitidas e os prejuízos materiais comprovadamente causados pelo COMPRADOR.</p>
          <p>3.7. Caso a recusa do crédito ocorra sem culpa das partes e antes da entrega do VEÍCULO, elas poderão encerrar a operação mediante restituição simples dos valores e bens recebidos, sem multa compensatória.</p>

          <h3>Cláusula 4ª — Veículo dado em pagamento</h3>
          <p>4.1. Esta cláusula será aplicável quando uma das formas de pagamento indicadas no caput estiver identificada como troca, permuta, dação em pagamento ou expressão equivalente.</p>
          <p>4.2. O COMPRADOR declara que o veículo entregue em pagamento: a) é de sua legítima propriedade ou está sob sua legítima disponibilidade para alienação; b) possui documentação regular e apta à transferência; c) encontra-se livre de restrições, bloqueios, gravames e débitos, exceto aqueles previamente informados e expressamente aceitos pela VENDEDORA; d) não possui multas, tributos, licenciamentos ou encargos anteriores não informados; e) não possui adulteração de chassi, motor, identificação ou quilometragem; f) não possui histórico relevante de leilão, enchente, indenização integral, perda total, reparo estrutural ou sinistro omitido da VENDEDORA; e g) não apresenta defeito grave conhecido que tenha sido ocultado durante a avaliação.</p>
          <p>4.3. A entrega física do veículo não substitui a obrigação do COMPRADOR de fornecer documentos, assinar a transferência e praticar todos os atos necessários para que a VENDEDORA possa registrar e comercializar regularmente o bem recebido.</p>
          <p>4.4. O COMPRADOR continuará responsável por multas, tributos, bloqueios, restrições, gravames, vícios de propriedade e fatos ocorridos antes da entrega do veículo em pagamento, ainda que constatados ou lançados posteriormente.</p>
          <p>4.5. Se for identificada informação relevante omitida, débito, restrição ou irregularidade preexistente, a VENDEDORA poderá exigir, conforme o caso: a) a imediata regularização; b) o pagamento do débito ou da diferença de valor objetivamente apurada; c) a substituição dessa parte do pagamento; ou d) a resolução do negócio, quando a irregularidade inviabilizar a operação.</p>
          <p>4.6. Qualquer diferença de valor deverá ser fundamentada em consulta, documento, laudo, orçamento ou outra comprovação objetiva, não sendo permitida redução arbitrária do valor anteriormente atribuído.</p>

          <h3>Cláusula 5ª — Condições do veículo e vistoria</h3>
          <p>5.1. O COMPRADOR declara que recebeu oportunidade para examinar o VEÍCULO, verificar suas condições aparentes, realizar teste de condução e, caso deseje, submetê-lo à análise de profissional de sua confiança.</p>
          <p>5.2. A aquisição de veículo usado pressupõe a existência de desgaste natural compatível com idade, quilometragem, conservação e uso anterior.</p>
          <p>5.3. A vistoria, o teste e o recebimento do VEÍCULO não representam renúncia à garantia legal nem impedem reclamação sobre vício oculto comprovadamente preexistente à entrega.</p>
          <p>5.4. O laudo cautelar, quando apresentado, constitui importante elemento de verificação documental e estrutural, mas não substitui integralmente avaliação mecânica, elétrica ou eletrônica.</p>
          <p>5.5. Pequenas marcas de uso, riscos, desgastes estéticos, variações de acabamento e demais condições aparentes compatíveis com veículo usado não serão consideradas vício oculto.</p>

          <h3>Cláusula 6ª — Declarações da vendedora</h3>
          <p>6.1. A VENDEDORA declara possuir legítima disponibilidade comercial sobre o VEÍCULO e poderes para realizar a presente venda.</p>
          <p>6.2. A VENDEDORA fornecerá os documentos que estiverem sob sua responsabilidade e forem necessários para a regular transferência do VEÍCULO.</p>
          <p>6.3. O VEÍCULO será entregue sem débitos, multas, tributos, bloqueios ou gravames anteriores que não tenham sido previamente informados e aceitos pelo COMPRADOR.</p>
          <p>6.4. Caso o VEÍCULO esteja registrado em nome de fornecedor, consignante ou terceiro integrante da cadeia comercial, a VENDEDORA continuará responsável perante o COMPRADOR pela regularidade da documentação e pela viabilização da transferência.</p>

          <h3>Cláusula 7ª — Entrega, posse e riscos</h3>
          <p>7.1. A entrega do VEÍCULO ocorrerá após: a) a assinatura deste contrato; b) a confirmação das formas de pagamento exigíveis até a entrega; c) a formalização do financiamento ou gravame, quando aplicável; e d) o cumprimento das demais exigências documentais da operação.</p>
          <p>7.2. A data e o horário da retirada, entrega ou disponibilização do VEÍCULO serão comprovados por termo de entrega, recibo, documento eletrônico, fotografia, comunicação escrita ou outro registro idôneo.</p>
          <p>7.3. Até a efetiva entrega, os riscos de guarda do VEÍCULO permanecerão com a VENDEDORA.</p>
          <p>7.4. Após a entrega, o COMPRADOR assume os riscos relacionados à circulação, guarda, colisões, furtos, roubos, multas, pedágios, uso, conservação e eventos posteriores, ressalvados os vícios comprovadamente preexistentes.</p>
          <p>7.5. A retirada por pessoa autorizada pelo COMPRADOR produzirá os mesmos efeitos da entrega direta.</p>

          <h3>Cláusula 8ª — Transferência e comunicação de venda</h3>
          <p>8.1. O COMPRADOR compromete-se a fornecer os documentos e assinar os instrumentos necessários à transferência da propriedade do VEÍCULO.</p>
          <p>8.2. O COMPRADOR deverá concluir a transferência dentro do prazo estabelecido pela legislação de trânsito.</p>
          <p>8.3. A VENDEDORA ou o proprietário registral realizará a comunicação de venda e conservará o respectivo comprovante, quando aplicável.</p>
          <p>8.4. A parte que deixar de fornecer documento, assinatura ou providência de sua responsabilidade deverá regularizar a situação e ressarcir multas, despesas e prejuízos diretos comprovadamente decorrentes de sua omissão.</p>

          <h3>Cláusula 9ª — Multas, tributos e encargos</h3>
          <p>9.1. As multas, tributos, pedágios e demais encargos decorrentes de fatos anteriores à entrega do VEÍCULO serão de responsabilidade da VENDEDORA ou do responsável anterior.</p>
          <p>9.2. As obrigações decorrentes de fatos posteriores à entrega serão de responsabilidade do COMPRADOR, ainda que lançadas ou cobradas em nome da VENDEDORA ou do antigo proprietário.</p>
          <p>9.3. A parte cobrada por obrigação pertencente à outra deverá encaminhar o respectivo comprovante, concedendo prazo razoável para pagamento, defesa ou indicação do condutor.</p>

          <h3>Cláusula 10ª — Garantia legal</h3>
          <p>10.1. Quando caracterizada relação de consumo, o VEÍCULO estará sujeito à garantia legal prevista na legislação, independentemente da existência de garantia contratual adicional.</p>
          <p>10.2. A garantia de veículo usado não significa obrigação de entregá-lo nas mesmas condições de um veículo novo.</p>

          <h3>Cláusula 11ª — Desgaste natural e fatos posteriores</h3>
          <p>11.1. Não caracterizam automaticamente vício de responsabilidade da VENDEDORA: a) desgaste natural compatível com idade e quilometragem; b) manutenção periódica vencida após a entrega; c) substituição ordinária de pneus, pastilhas, discos de freio, palhetas, filtros, fluidos, lâmpadas, bateria de baixa tensão e itens semelhantes; d) danos decorrentes de colisão, alagamento, combustível inadequado, sobrecarga, competição ou condução imprudente; e) defeitos causados por instalação de acessórios, modificações ou reparos realizados após a entrega; f) agravamento causado pela continuidade de uso após alerta no painel, ruído ou sinal evidente de falha; ou g) ausência de manutenção recomendada pelo fabricante.</p>
          <p>11.2. As exclusões acima somente serão aplicáveis quando houver relação entre a conduta, o desgaste ou o fato posterior e o defeito reclamado.</p>
          <p>11.3. A simples classificação de determinado componente como item de desgaste não afasta responsabilidade por falha anormal, prematura ou comprovadamente preexistente.</p>

          <h3>Cláusula 12ª — Procedimento em caso de defeito</h3>
          <p>12.1. Ao identificar possível defeito, o COMPRADOR deverá comunicar a VENDEDORA pelo telefone ou e-mail indicado no caput, informando os sintomas, a quilometragem e, quando possível, encaminhando fotos, vídeos ou alertas apresentados pelo VEÍCULO.</p>
          <p>12.2. Havendo risco de segurança ou agravamento, o COMPRADOR deverá interromper a utilização do VEÍCULO.</p>
          <p>12.3. A VENDEDORA indicará procedimento para inspeção e diagnóstico em oficina tecnicamente adequada, própria ou terceirizada.</p>
          <p>12.4. Antes de realizar reparo por conta própria, o COMPRADOR deverá permitir que a VENDEDORA examine o VEÍCULO, salvo situação emergencial necessária para segurança ou prevenção de dano maior.</p>
          <p>12.5. Reparos realizados por terceiros sem prévia comunicação não gerarão reembolso automático. O ressarcimento dependerá da comprovação: a) da existência do vício coberto; b) da necessidade ou urgência do reparo; c) do nexo entre o defeito e a venda; e d) da razoabilidade do valor pago.</p>
          <p>12.6. O COMPRADOR deverá preservar notas fiscais, laudos, ordens de serviço e, quando possível, as peças substituídas.</p>

          <h3>Cláusula 13ª — Reparo e soluções legais</h3>
          <p>13.1. Reconhecida a existência de vício coberto, a VENDEDORA providenciará o reparo sem custo ao COMPRADOR <strong>exclusivamente em oficina própria da VENDEDORA</strong>, salvo autorização expressa e por escrito em sentido diverso.</p>
          <p>13.2. Reparos realizados em qualquer outra oficina sem autorização prévia e expressa da VENDEDORA não serão reembolsados nem cobertos pela garantia.</p>
          <p>13.3. A VENDEDORA terá o prazo previsto na legislação para solucionar o vício, contado na forma legal e sem reinício artificial por sucessivas tentativas relacionadas ao mesmo problema.</p>
          <p>13.4. Não sendo o vício solucionado dentro do prazo legal, o COMPRADOR poderá exercer as alternativas previstas na legislação aplicável.</p>
          <p>13.5. A VENDEDORA não se obriga automaticamente a disponibilizar veículo reserva, salvo ajuste escrito em sentido diferente.</p>
          <p>13.6. A ausência de veículo reserva não exclui eventual ressarcimento de despesas materiais necessárias, razoáveis e comprovadamente decorrentes de vício de responsabilidade da VENDEDORA.</p>

          <h3>Cláusula 14ª — Inadimplemento e resolução</h3>
          <p>14.1. O descumprimento de obrigação essencial autoriza a parte prejudicada a notificar a outra para regularização, quando possível.</p>
          <p>14.2. Não ocorrendo a regularização, o contrato poderá ser resolvido, com devolução dos valores e bens recebidos, observadas as responsabilidades de cada parte.</p>
          <p>14.3. Se houver resolução após a entrega, o COMPRADOR deverá devolver o VEÍCULO com seus documentos, chaves e acessórios, respondendo por multas, danos, modificações e eventos posteriores ocorridos durante sua posse.</p>
          <p>14.4. A VENDEDORA deverá restituir os valores que forem legalmente devidos e, quando houver veículo recebido em pagamento, devolvê-lo.</p>
          <p>14.5. Caso o veículo recebido em pagamento já tenha sido legitimamente vendido a terceiro, será restituído ao COMPRADOR o valor que lhe foi atribuído na composição do pagamento, corrigido monetariamente, ressalvada determinação legal ou acordo diverso.</p>

          <h3>Cláusula 15ª — Mora e encargos</h3>
          <p>15.1. O atraso no pagamento de qualquer valor devido pelo COMPRADOR acarretará, sobre a parcela vencida: a) multa moratória de 2%; b) juros de mora de 1% ao mês, calculados proporcionalmente; e c) correção monetária pelo IPCA.</p>
          <p>15.2. Os encargos não afastam o direito da VENDEDORA de exigir o cumprimento da obrigação ou resolver o contrato quando o inadimplemento for relevante.</p>
          <p>15.3. Não haverá cobrança cumulativa de penalidades que resulte em vantagem manifestamente excessiva.</p>

          <h3>Cláusula 16ª — Comunicações</h3>
          <p>16.1. Serão considerados canais oficiais de comunicação os dados de telefone e e-mail indicados no caput.</p>
          <p>16.2. As partes deverão comunicar eventual alteração de seus contatos.</p>
          <p>16.3. Mensagens, protocolos, ordens de serviço, e-mails e documentos eletrônicos poderão ser utilizados como prova das comunicações e providências relacionadas à operação.</p>

          <h3>Cláusula 17ª — Proteção de dados</h3>
          <p>17.1. Os dados pessoais fornecidos poderão ser utilizados para faturamento, análise de crédito, transferência, prevenção a fraudes, atendimento, garantia e cumprimento de obrigações legais e contratuais.</p>
          <p>17.2. Os dados poderão ser compartilhados, na medida necessária, com instituições financeiras, despachantes, oficinas, seguradoras, plataformas de assinatura e órgãos públicos.</p>

          <h3>Cláusula 18ª — Assinatura eletrônica</h3>
          <p>18.1. As partes reconhecem como válidas as assinaturas físicas ou eletrônicas capazes de identificar os signatários e preservar a integridade do documento.</p>
          <p>18.2. A assinatura realizada por plataforma eletrônica, com registro de autenticação e trilha de auditoria, produzirá os mesmos efeitos da assinatura física.</p>
          <p>18.3. Este contrato será considerado celebrado na data da última assinatura eletrônica.</p>

          <h3>Cláusula 19ª — Disposições gerais e foro</h3>
          <p>19.1. A eventual invalidade de uma disposição não prejudicará as demais cláusulas do contrato.</p>
          <p>19.2. A tolerância de uma parte em relação ao descumprimento da outra não significará renúncia, alteração ou novação.</p>
          <p>19.3. Nas relações de consumo, será observado o foro legalmente competente e assegurado ao COMPRADOR o exercício dos direitos previstos na legislação.</p>
          <p>19.4. Nas operações em que não houver relação de consumo, fica eleito o foro da comarca de Ribeirão Preto/SP, ressalvada competência legal obrigatória.</p>
        </div>

        <div className="signature-section">
          <p style={{ textAlign: 'justify', margin: '8px 0' }}>
            E, por estarem assim justos e contratados, assinam o presente instrumento, em via única,
            eletrônica ou física, de igual teor e forma.
          </p>
          <div className="place">
            Ribeirão Preto/SP, {dateLong(v.sale_date)}.
          </div>
          <br /><br /><br />
          <div className="sig-row">
            <div className="sig-box">
              <div className="sig-line">
                <div className="sig-name">Matelli Motors Ltda</div>
                <div className="sig-doc">CNPJ 62.352.611/0001-56</div>
                <div className="sig-role">Vendedora</div>
              </div>
            </div>
            <div className="sig-box">
              <div className="sig-line">
                <div className="sig-name">{v.buyer_name || '—'}</div>
                <div className="sig-doc">CPF/CNPJ {v.buyer_document ? formatDocBR(v.buyer_document) : '—'}</div>
                <div className="sig-role">Comprador</div>
              </div>
            </div>
          </div>
        </div>

        <div className="doc-footer">
          Matelli Motors Ltda · CNPJ 62.352.611/0001-56<br />
          Rua Triunfo, 1.349, sala 315 · Jardim Botânico · Ribeirão Preto/SP · CEP 14.021-612
        </div>
      </div>
    </>
  );
}
